import React, { useEffect, useState } from "react";
import { emitCustomEvent } from "react-custom-events";
import { Modal } from "antd";
import ReactSelect from "react-select";
import Brand from "../../api/Brand";
import SuperUser from "../../api/SuperUser";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

const ChangeBrandModal = ({ company = null, visible, title, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [brands, setBrands] = useState([]);
  const [isEnterprise] = useState(company?.is_enterprise === 1);

  useEffect(() => {
    if (company) {
      loadBrands();
    }
  }, [company]);
  const loadBrands = () => {
    setBrandsLoading(true);
    Brand.getAll()
      .then((response) => {
        setBrands(response.data.data);
        if (response.data.data.length > 0) {
          const _selectedBrands = filterSelectedBrands(response.data.data);
          setSelectedBrands(_selectedBrands);
        }
      })
      .catch((error) => {
        toast.error(t("common.something-went-wrong"));
      })
      .finally(() => {
        setBrandsLoading(false);
      });
  };

  const filterSelectedBrands = (_brands) => {
    const _selectedbrands = company?.locations.map((loc) => {
      const _selectedbrand = _brands.find((b) => b?.id === loc?.brand_id);
      if (_selectedbrand) {
        return { label: _selectedbrand?.name, value: _selectedbrand?.id };
      } else {
        return;
      }
    });
    const _tempSelectedBrand = _selectedbrands.filter((i) => i?.value);
    return _tempSelectedBrand;
  };

  const updateBrandsCompany = () => {
    if (!selectedBrands) {
      toast.error(t("companies.please-select-brand"));
      return;
    }

    setLoading(true);
    const body = {
      company_id: company?.id,
      brand_id: selectedBrands?.value,
    };

    SuperUser.updateBrandCompany(body)
      .then(() => {
        toast.success(t("companies.brand-updated-successfully"));
        emitCustomEvent("closeChangeBrandModal");
        onClose();
      })
      .catch((error) => {
        if (error?.response?.status === 403) {
          toast.error(t("common.permission-denied"));
        } else if (error?.response?.status === 422) {
          toast.error(t("companies.invalid-brand-selection"));
        } else {
          toast.error(t("common.something-went-wrong"));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      title={null}
      visible={visible}
      onCancel={onClose}
      closable={false}
      footer={null}
    >
      <div className="w-full">
        {/* Modal Header */}
        <div className="relative w-full bg-primary-500 px-6 py-4">
          <h2 className="text-xl text-white font-medium">{title}</h2>
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white transition-colors"
            onClick={onClose}
          >
            <i className="far fa-times text-xl" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          <div className="space-y-6">
            {isEnterprise && (
              <div>
                <label className="block text-sm font-medium text-gray-900">
                  Select Brand
                </label>
                {brandsLoading ? (
                  <div className="animate-pulse">
                    <div className="h-10 bg-gray-200"></div>
                    <div className="mt-2 h-4 bg-gray-200"></div>
                  </div>
                ) : (
                  <>
                    <ReactSelect
                      value={selectedBrands}
                      placeholder="Choose a brand..."
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isSearchable
                      isClearable
                      isDisabled={brandsLoading}
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 8,
                        colors: {
                          ...theme.colors,
                          primary: "#7D66D3",
                          primary75: "#9C8CDC",
                          primary50: "#A899E2",
                          primary25: "#EDE9F8",
                        },
                      })}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "40px",
                          boxShadow: "none",
                          borderColor: "#E5E7EB",
                          "&:hover": {
                            borderColor: "#7D66D3",
                          },
                        }),
                      }}
                      onChange={(selected) => setSelectedBrands(selected)}
                      options={brands.map((brand) => ({
                        value: brand.id,
                        label: brand.name,
                      }))}
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      Select the brand you want to associate with this company
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3">
            <button
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              onClick={() => onClose()}
            >
              {t("common.cancel")}
            </button>
            <button
              className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg bg-primary-500 text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 disabled:cursor-not-allowed inline-flex items-center justify-center"
              onClick={(e) => {
                e.preventDefault();
                updateBrandsCompany();
              }}
              disabled={loading || brandsLoading || !selectedBrands}
            >
              {loading ? (
                <i className="fad fa-circle-notch fa-spin mr-2" />
              ) : null}
              {t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ChangeBrandModal;
